import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  EnvelopeIcon,
  ArrowPathIcon,
  CheckCircleIcon,
  XCircleIcon,
  UserGroupIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';
import { useAuth } from '../context/AuthContext';
import { teamAPI } from '../services/api';
import Logo from '../components/common/Logo';

export default function AcceptInvitePage() {
  const { token } = useParams();
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();

  const [invitation, setInvitation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [accepting, setAccepting] = useState(false);
  const [success, setSuccess] = useState(false);

  // Registration form state (for new users)
  const [name, setName] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [formError, setFormError] = useState(null);

  useEffect(() => {
    fetchInvitation();
  }, [token]);

  const fetchInvitation = async () => {
    try {
      setLoading(true);
      const response = await teamAPI.getInvitationByToken(token);
      setInvitation(response.data.data);
    } catch (err) {
      setError('Invitation not found or has expired');
    } finally {
      setLoading(false);
    }
  };

  const handleAcceptAsExistingUser = async () => {
    try {
      setAccepting(true);
      setFormError(null);
      await teamAPI.acceptInvitation(token);
      setSuccess(true);
      setTimeout(() => navigate('/dashboard'), 2000);
    } catch (err) {
      setFormError(err.response?.data?.message || 'Failed to accept invitation');
    } finally {
      setAccepting(false);
    }
  };

  const handleRegisterAndAccept = async (e) => {
    e.preventDefault();
    setFormError(null);

    if (!name.trim()) {
      setFormError('Name is required');
      return;
    }

    if (password.length < 8) {
      setFormError('Password must be at least 8 characters');
      return;
    }

    if (password !== confirmPassword) {
      setFormError('Passwords do not match');
      return;
    }

    try {
      setAccepting(true);
      await teamAPI.acceptInvitationWithRegistration(token, { name, password });
      setSuccess(true);
      setTimeout(() => navigate('/login'), 2000);
    } catch (err) {
      setFormError(err.response?.data?.message || 'Failed to create account');
    } finally {
      setAccepting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-slate-900">
        <ArrowPathIcon className="h-8 w-8 animate-spin text-indigo-600" />
      </div>
    );
  }

  if (error || !invitation) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-slate-900 px-4">
        <div className="max-w-md w-full text-center">
          <div className="mx-auto w-16 h-16 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center mb-6">
            <XCircleIcon className="h-8 w-8 text-red-600 dark:text-red-400" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
            Invalid Invitation
          </h1>
          <p className="text-gray-500 dark:text-gray-400 mb-6">
            {error || 'This invitation link is invalid or has expired.'}
          </p>
          <Link
            to="/login"
            className="inline-flex items-center px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
          >
            Go to Login
          </Link>
        </div>
      </div>
    );
  }

  if (!invitation.isValid) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-slate-900 px-4">
        <div className="max-w-md w-full text-center">
          <div className="mx-auto w-16 h-16 rounded-full bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center mb-6">
            <ExclamationTriangleIcon className="h-8 w-8 text-amber-600 dark:text-amber-400" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
            Invitation {invitation.status === 'expired' ? 'Expired' : 'Invalid'}
          </h1>
          <p className="text-gray-500 dark:text-gray-400 mb-6">
            {invitation.status === 'expired'
              ? 'This invitation has expired. Please contact the team administrator for a new invitation.'
              : `This invitation has been ${invitation.status}.`}
          </p>
          <Link
            to="/login"
            className="inline-flex items-center px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
          >
            Go to Login
          </Link>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-slate-900 px-4">
        <div className="max-w-md w-full text-center">
          <div className="mx-auto w-16 h-16 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center mb-6">
            <CheckCircleIcon className="h-8 w-8 text-green-600 dark:text-green-400" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
            Welcome to the Team!
          </h1>
          <p className="text-gray-500 dark:text-gray-400 mb-6">
            You have successfully joined {invitation.tenant?.name || 'the team'}.
            Redirecting...
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-slate-900 px-4 py-12">
      <div className="max-w-md w-full">
        <div className="text-center mb-8">
          <Logo className="h-10 w-auto mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
            You're Invited!
          </h1>
        </div>

        <div className="bg-white dark:bg-slate-800 rounded-xl shadow-lg border border-gray-200 dark:border-slate-700 p-6">
          {/* Invitation Details */}
          <div className="flex items-center gap-4 pb-6 border-b border-gray-200 dark:border-slate-700">
            <div className="p-3 bg-indigo-100 dark:bg-indigo-900/30 rounded-lg">
              <UserGroupIcon className="h-8 w-8 text-indigo-600 dark:text-indigo-400" />
            </div>
            <div>
              <h2 className="font-semibold text-gray-900 dark:text-white">
                {invitation.tenant?.name || 'Team Invitation'}
              </h2>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                {invitation.invitedBy?.name || invitation.invitedBy?.email} invited you to join
                as {invitation.role === 'admin' ? 'an' : 'a'}{' '}
                <span className="capitalize font-medium">{invitation.role}</span>
              </p>
            </div>
          </div>

          {/* Email Display */}
          <div className="py-4">
            <div className="flex items-center gap-3 p-3 bg-gray-50 dark:bg-slate-700/50 rounded-lg">
              <EnvelopeIcon className="h-5 w-5 text-gray-400" />
              <span className="text-gray-900 dark:text-white">{invitation.email}</span>
            </div>
          </div>

          {formError && (
            <div className="mb-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3">
              <p className="text-sm text-red-800 dark:text-red-400">{formError}</p>
            </div>
          )}

          {isAuthenticated ? (
            // Authenticated user - just accept
            <div className="space-y-4">
              {user.email.toLowerCase() === invitation.email.toLowerCase() ? (
                <button
                  onClick={handleAcceptAsExistingUser}
                  disabled={accepting}
                  className="w-full inline-flex items-center justify-center gap-2 px-4 py-3 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors disabled:opacity-50"
                >
                  {accepting ? (
                    <>
                      <ArrowPathIcon className="h-5 w-5 animate-spin" />
                      Accepting...
                    </>
                  ) : (
                    <>
                      <CheckCircleIcon className="h-5 w-5" />
                      Accept Invitation
                    </>
                  )}
                </button>
              ) : (
                <div className="text-center py-4">
                  <p className="text-amber-600 dark:text-amber-400 mb-4">
                    This invitation was sent to a different email address.
                  </p>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    You're logged in as <strong>{user.email}</strong>, but this invitation
                    was sent to <strong>{invitation.email}</strong>.
                  </p>
                </div>
              )}
            </div>
          ) : (
            // Not authenticated - show registration form
            <form onSubmit={handleRegisterAndAccept} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Your Name
                </label>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="John Doe"
                  className="w-full px-3 py-2 border border-gray-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  disabled={accepting}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Create Password
                </label>
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="At least 8 characters"
                  className="w-full px-3 py-2 border border-gray-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  disabled={accepting}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Confirm Password
                </label>
                <input
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Confirm your password"
                  className="w-full px-3 py-2 border border-gray-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  disabled={accepting}
                />
              </div>

              <button
                type="submit"
                disabled={accepting}
                className="w-full inline-flex items-center justify-center gap-2 px-4 py-3 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors disabled:opacity-50"
              >
                {accepting ? (
                  <>
                    <ArrowPathIcon className="h-5 w-5 animate-spin" />
                    Creating Account...
                  </>
                ) : (
                  <>
                    <CheckCircleIcon className="h-5 w-5" />
                    Create Account & Join
                  </>
                )}
              </button>

              <p className="text-center text-sm text-gray-500 dark:text-gray-400">
                Already have an account?{' '}
                <Link to="/login" className="text-indigo-600 hover:text-indigo-500">
                  Log in
                </Link>
              </p>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
